/**
 * Two independent timers, and the reason there are two.
 *
 * The byte watchdog is reset by any byte, keepalive comments included. When it
 * fires the connection itself has gone quiet, and the right response is to
 * reconnect. The content watchdog is reset only by data frames, and it is
 * armed only while the stream is in a phase that should be producing them.
 * When it fires the connection is healthy and the model is not producing,
 * which needs different copy and different recovery: reconnecting a healthy
 * connection accomplishes nothing.
 *
 * A server sending keepalives and no tokens trips exactly one of the two.
 * Scenarios `stalled_no_bytes` and `stalled_keepalive_only` prove they fire
 * independently.
 *
 * Timers are injected. This package compiles without the DOM library and
 * without the Node types, so it cannot name `setTimeout` itself, and the
 * injection is also what lets the tests drive time by hand.
 */

import type { StreamPhase } from './reducer.ts';

export const BYTE_TIMEOUT_MS = 30_000;
export const CONTENT_TIMEOUT_MS = 20_000;

/** The two members of the platform timer surface this package uses. */
export interface TimerHost {
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export interface WatchdogOptions {
  timers: TimerHost;
  /** Defaults to BYTE_TIMEOUT_MS. */
  byteTimeoutMs?: number;
  /** Defaults to CONTENT_TIMEOUT_MS. */
  contentTimeoutMs?: number;
  onByteTimeout: () => void;
  onContentTimeout: () => void;
}

export interface Watchdogs {
  /** Arm the byte watchdog. The content watchdog arms when the phase warrants it. */
  start(): void;
  /** Disarm both. Safe to call twice. */
  stop(): void;
  /** Any byte arrived, keepalives included. Resets the byte watchdog only. */
  bytesSeen(): void;
  /** A data frame arrived. Resets the content watchdog only. */
  contentSeen(): void;
  /**
   * The stream phase changed. The content watchdog runs only during
   * `streaming` and `thinking`: a queue wait or a reconnect is allowed to be
   * quiet, and holding a stall timer over it would report the absence of
   * output nobody was promised.
   */
  phaseChanged(phase: StreamPhase): void;
}

const CONTENT_PHASES: readonly StreamPhase[] = ['streaming', 'thinking'];

export function createWatchdogs(options: WatchdogOptions): Watchdogs {
  const { timers, onByteTimeout, onContentTimeout } = options;
  const byteTimeoutMs = options.byteTimeoutMs ?? BYTE_TIMEOUT_MS;
  const contentTimeoutMs = options.contentTimeoutMs ?? CONTENT_TIMEOUT_MS;

  let running = false;
  let contentActive = false;
  let byteHandle: unknown = null;
  let contentHandle: unknown = null;

  const clearByte = (): void => {
    if (byteHandle !== null) {
      timers.clearTimeout(byteHandle);
      byteHandle = null;
    }
  };

  const clearContent = (): void => {
    if (contentHandle !== null) {
      timers.clearTimeout(contentHandle);
      contentHandle = null;
    }
  };

  const armByte = (): void => {
    clearByte();
    byteHandle = timers.setTimeout(() => {
      byteHandle = null;
      onByteTimeout();
    }, byteTimeoutMs);
  };

  const armContent = (): void => {
    clearContent();
    contentHandle = timers.setTimeout(() => {
      contentHandle = null;
      onContentTimeout();
    }, contentTimeoutMs);
  };

  return {
    start(): void {
      running = true;
      armByte();
      if (contentActive) armContent();
    },

    stop(): void {
      running = false;
      clearByte();
      clearContent();
    },

    bytesSeen(): void {
      if (running) armByte();
    },

    contentSeen(): void {
      if (running && contentActive) armContent();
    },

    phaseChanged(phase: StreamPhase): void {
      const active = CONTENT_PHASES.includes(phase);
      if (active === contentActive) return;
      contentActive = active;
      if (!running) return;
      if (active) {
        armContent();
      } else {
        clearContent();
      }
    },
  };
}
